import { PGlite } from '@electric-sql/pglite';
import { createClient } from '@supabase/supabase-js';
import { initializationError, lifecycleError, staleOperationError, unsupported } from './errors.js';
import { DEMO_URL } from './schema.js';
import { createDemoFetch } from './transport.js';
import { defaultDemoUser, } from './types.js';
async function closeQuietly(db) {
    if (!db) {
        return;
    }
    try {
        await db.close();
    }
    catch {
        // Disposal should not hide the original lifecycle outcome.
    }
}
export class DemoRuntime {
    user;
    tables;
    definition;
    state = 'initializing';
    error = null;
    db = null;
    epoch = 0;
    operationQueue = Promise.resolve();
    resetPromise = null;
    generation = 0;
    inFlight = 0;
    idleWaiters = [];
    inFlightWaiters = [];
    listeners = new Set();
    writeBarrier = null;
    releaseWriteBarrier = null;
    supabaseClient = null;
    closed = false;
    diagnostics = { observedUnsupported: [] };
    constructor(definition) {
        this.definition = definition;
        this.tables = definition.tables;
        this.user = definition.user ?? defaultDemoUser;
    }
    get supabase() {
        if (!this.supabaseClient) {
            const recordRejectedRealtime = () => this.recordUnsupported('Realtime is unsupported.', '/realtime/v1');
            class RejectedRealtimeSocket extends globalThis.EventTarget {
                CONNECTING = 0;
                OPEN = 1;
                CLOSING = 2;
                CLOSED = 3;
                readyState = 3;
                url = '';
                protocol = '';
                onopen = null;
                onmessage = null;
                onclose = null;
                onerror = null;
                constructor() {
                    super();
                    recordRejectedRealtime();
                    throw unsupported('Realtime is unsupported. Use the local database query API.');
                }
                close() { }
                send() { throw unsupported('Realtime is unsupported.'); }
            }
            this.supabaseClient = createClient(DEMO_URL, 'demo-key', {
                auth: {
                    persistSession: false,
                    autoRefreshToken: false,
                    detectSessionInUrl: false,
                },
                realtime: { transport: RejectedRealtimeSocket },
                db: { schema: 'public' },
                global: {
                    fetch: createDemoFetch(this),
                },
            });
        }
        return this.supabaseClient;
    }
    getState() {
        return this.state;
    }
    getError() {
        return this.error;
    }
    snapshot() {
        return {
            state: this.state,
            error: this.error,
            user: this.user,
            generation: this.generation,
        };
    }
    subscribe(listener) {
        this.listeners.add(listener);
        listener(this.snapshot());
        return () => {
            this.listeners.delete(listener);
        };
    }
    async initialize() {
        this.state = 'initializing';
        this.error = null;
        this.notify();
        try {
            const candidate = await this.bootEngine();
            if (this.closed) {
                await closeQuietly(candidate);
                throw lifecycleError('DemoPack was disposed during initialization.', 'disposed');
            }
            this.db = candidate;
            this.state = 'ready';
            this.generation += 1;
            this.notify();
        }
        catch (cause) {
            if (this.closed) {
                throw cause instanceof Error ? cause : lifecycleError('DemoPack was disposed during initialization.', 'disposed');
            }
            const error = cause instanceof Error && 'code' in cause ? cause : initializationError(cause);
            this.state = 'failed';
            this.error = error;
            this.notify();
            throw error;
        }
    }
    reset() {
        if (!this.resetPromise) {
            this.resetPromise = this.performReset().finally(() => { this.resetPromise = null; });
        }
        return this.resetPromise;
    }
    async performReset() {
        if (this.state === 'disposed') {
            throw lifecycleError('Cannot reset a disposed DemoPack instance.', 'disposed');
        }
        this.epoch += 1;
        this.releaseHeldWrites();
        this.state = 'resetting';
        this.error = null;
        this.notify();
        const previous = this.db;
        this.db = null;
        this.operationQueue = Promise.resolve();
        try {
            const next = await this.bootEngine();
            if (this.closed) {
                await closeQuietly(next);
                await closeQuietly(previous);
                throw lifecycleError('DemoPack was disposed during reset.', 'disposed');
            }
            this.db = next;
            this.state = 'ready';
            this.generation += 1;
            this.error = null;
            this.notify();
            void this.closeWhenIdle(previous);
        }
        catch (cause) {
            await this.closeWhenIdle(previous);
            if (this.closed) {
                throw cause instanceof Error ? cause : lifecycleError('DemoPack was disposed during reset.', 'disposed');
            }
            const error = cause instanceof Error && 'code' in cause ? cause : initializationError(cause);
            this.state = 'failed';
            this.error = error;
            this.notify();
            throw error;
        }
    }
    async dispose() {
        this.closed = true;
        this.epoch += 1;
        this.state = 'disposed';
        this.releaseHeldWrites();
        const current = this.db;
        this.db = null;
        this.notify();
        await this.closeWhenIdle(current);
    }
    recordUnsupported(message, path) {
        this.diagnostics.observedUnsupported.push({
            message,
            path,
            at: new Date().toISOString(),
        });
    }
    async waitForInFlight() {
        if (this.inFlight > 0) {
            return;
        }
        await new Promise((resolve) => {
            this.inFlightWaiters.push(resolve);
        });
    }
    async holdWrites() {
        if (this.releaseWriteBarrier) {
            return this.releaseWriteBarrier;
        }
        let release;
        this.writeBarrier = new Promise((resolve) => {
            release = resolve;
        });
        this.releaseWriteBarrier = () => {
            this.writeBarrier = null;
            this.releaseWriteBarrier = null;
            release();
        };
        return this.releaseWriteBarrier;
    }
    async runOperation(fn) {
        if (this.state === 'disposed') {
            throw lifecycleError('DemoPack instance is disposed.', 'disposed');
        }
        if (this.state !== 'ready') {
            throw lifecycleError(`DemoPack is not ready for database operations (state: ${this.state}).`, this.state);
        }
        const epoch = this.epoch;
        const db = this.db;
        if (!db) {
            throw lifecycleError('DemoPack has no active engine.', this.state);
        }
        const preceding = this.operationQueue;
        let releaseOperation;
        this.operationQueue = new Promise((resolve) => { releaseOperation = resolve; });
        this.inFlight += 1;
        const entered = this.inFlightWaiters.splice(0);
        for (const waiter of entered) {
            waiter();
        }
        try {
            await preceding;
            if (this.writeBarrier) {
                await this.writeBarrier;
            }
            if (this.epoch !== epoch) {
                throw staleOperationError();
            }
            const result = await fn(db);
            if (this.epoch !== epoch) {
                throw staleOperationError();
            }
            return result;
        }
        catch (error) {
            if (this.epoch !== epoch) {
                throw staleOperationError();
            }
            throw error;
        }
        finally {
            releaseOperation();
            this.inFlight -= 1;
            if (this.inFlight === 0) {
                const waiters = this.idleWaiters.splice(0);
                for (const waiter of waiters) {
                    waiter();
                }
            }
        }
    }
    toPublic() {
        return {
            supabase: this.supabase,
            user: this.user,
            getState: () => this.getState(),
            getError: () => this.getError(),
            getSnapshot: () => this.snapshot(),
            reset: () => this.reset(),
            dispose: () => this.dispose(),
            subscribe: (listener) => this.subscribe(listener),
            holdWrites: () => this.holdWrites(),
            waitForInFlight: () => this.waitForInFlight(),
            diagnostics: this.diagnostics,
        };
    }
    notify() {
        const snapshot = this.snapshot();
        for (const listener of this.listeners) {
            listener(snapshot);
        }
    }
    async bootEngine() {
        const candidate = new PGlite();
        try {
            await candidate.exec(this.definition.schemaSql);
            await this.definition.seed(candidate);
            return candidate;
        }
        catch (error) {
            await closeQuietly(candidate);
            throw error;
        }
    }
    releaseHeldWrites() {
        this.releaseWriteBarrier?.();
    }
    async closeWhenIdle(db) {
        if (!db) {
            return;
        }
        if (this.inFlight > 0) {
            await new Promise((resolve) => {
                this.idleWaiters.push(resolve);
            });
        }
        await closeQuietly(db);
    }
}
export async function createDemo(definition) {
    const runtime = new DemoRuntime(definition);
    try {
        await runtime.initialize();
    }
    catch (error) {
        await runtime.dispose();
        throw error;
    }
    return runtime.toPublic();
}
